/**
 * @copyright Copyright 2023-2025 Epic Systems Corporation
 * @file Download info for an app's showroom listing page
 * @module Epic.AppOrchard.Showroom.DownloadInfoSection
 */

import { QuestionOutlineIcon, TimeIcon } from "@chakra-ui/icons";
import {
	Box,
	Button,
	ButtonProps,
	ComponentWithAs,
	Flex,
	Stack,
	useBreakpointValue,
	VStack,
	Wrap,
} from "@chakra-ui/react";
import { showroomText } from "ao/chakraTheme";
import { AOLink, AOTooltip, EditableInstallStatus } from "ao/components/Core";
import { NotImplementingNowInfoBody } from "ao/components/Dashboard/widgets/nominatedApps/helperComponents/NotImplementingNowInfo";
import { DownloadApprovedStatus, InstallStatus } from "ao/data";
import { useSiteInfoState } from "ao/state/siteInfo";
import { IUserSecurity } from "ao/types";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import { getBaseUrl, isLocalhost, pluralIfNeeded } from "ao/utils/helpers";
import React, { FunctionComponent, memo, useMemo } from "react";
import { BiHeart } from "react-icons/bi";
import {
	NominateButton,
	NotImplementingNowButtonWrapper,
	ShowroomUpdateTokensButton,
} from "../AppHelperComponents";
import { UsageStatsSection } from "./UsageStatsSection";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	userSecurity: IUserSecurity;
	setAppDetails: (x: IShowroomAppDownloadModel) => void;
	reload: () => void;
	textColor: string;
	onlyThumbnail: boolean;
	subLogoElements: JSX.Element;
	isShowroom: boolean;
}

const actionButtonProps: ButtonProps = {
	variant: "link",
	colorScheme: "blue",
	fontSize: "1rem",
	fontWeight: "normal",
	size: "sm",
	padding: 0,
};

export const AppHeaderDetails: FunctionComponent<IProps> = memo((props) => {
	const {
		appDetails,
		userSecurity,
		setAppDetails,
		reload,
		textColor,
		onlyThumbnail,
		subLogoElements,
		isShowroom,
	} = props;

	//#region local state
	const isMobile = useBreakpointValue({ base: true, lg: false }) || false;

	const isCustomerOrEpicUser = userSecurity.isEpicCustomer || userSecurity.isEpicUser;

	const nominationCountTitle = userSecurity.isEpicUser
		? "The number of people who have nominated this product across all customer organizations."
		: "The number of people who have nominated this product from your organization.";

	const showSubLogoElements = !isMobile && !onlyThumbnail;

	const showNominationCountTag =
		isCustomerOrEpicUser &&
		appDetails.interestedCount > 0 &&
		!appDetails.isDownloaded &&
		!appDetails.isComingSoon;

	const showNotImplementingNowTag =
		!appDetails.isDownloaded && appDetails.notImplementingNowDetails !== null && !appDetails.isComingSoon;

	const showNotNowUserName =
		(userSecurity.canPurchase || userSecurity.isEpicUser) &&
		appDetails.notImplementingNowDetails?.userName;

	const isAppFromOwnOrg = userSecurity.userOrganizationId === appDetails.appModel.organizationId;
	const showUsageStats =
		(userSecurity.isEpicCustomer ||
			userSecurity.isEpicUser ||
			(userSecurity.isVendorUser && isAppFromOwnOrg)) &&
		appDetails.appId !== null &&
		!appDetails.isComingSoon;

	const canPurchase = userSecurity.isEpicCustomer && userSecurity.canPurchase;

	const isRemoteAccessing =
		userSecurity.userDBOrganizationId !== null &&
		userSecurity.userOrganizationId !== userSecurity.userDBOrganizationId;

	const canRequest =
		(!appDetails.isDownloaded || appDetails.vendorApproved === DownloadApprovedStatus.Overwritten) &&
		!isAppFromOwnOrg &&
		appDetails.appModel.showDownloadLink &&
		canPurchase &&
		!appDetails.isComingSoon;

	const canNominate =
		!isAppFromOwnOrg &&
		userSecurity.isEpicCustomer &&
		!userSecurity.canPurchase &&
		!appDetails.isDownloaded &&
		!appDetails.interestedOn &&
		!appDetails.notImplementingNowDetails &&
		!isRemoteAccessing &&
		!appDetails.isComingSoon;

	const canUpdateTokens =
		appDetails.appModel.showDownloadLink &&
		appDetails.isDownloaded &&
		appDetails.appModel.usesOAuth2 &&
		appDetails.vendorApproved !== DownloadApprovedStatus.Declined &&
		appDetails.vendorApproved !== DownloadApprovedStatus.Overwritten &&
		canPurchase &&
		!appDetails.appModel.requiresFutureEpicVersion;

	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);
	const isShowroomPublic = isShowroom && !isLocalhost() && !siteInfo.isAdminSite;

	const baseUrl = useMemo(() => (isShowroomPublic ? siteInfo.vendorServicesUrl : getBaseUrl()), [
		isShowroomPublic,
		siteInfo.vendorServicesUrl,
	]);

	const showroomBaseUrl = useMemo(() => getBaseUrl(), []);

	//#endregion

	return (
		<VStack wordBreak="break-word" spacing={0} alignItems={onlyThumbnail ? "center" : "stretch"}>
			{showSubLogoElements && subLogoElements}
			{showUsageStats && (
				<Box>
					<UsageStatsSection appDetails={appDetails} textColor={textColor} />
				</Box>
			)}

			<Stack spacing={0} direction={isMobile ? "column" : "row"}>
				{appDetails.isDownloaded &&
					userSecurity.canPurchase &&
					(appDetails.vendorApproved === DownloadApprovedStatus.Approved ||
						appDetails.vendorApproved === DownloadApprovedStatus.NonProdOnly) && (
						<Flex color={showroomText} justify={isMobile ? "center" : "left"} pt="5px">
							<Box>
								<EditableInstallStatus
									appId={appDetails.appModel.id}
									orgId={userSecurity.userOrganizationId ?? -1}
									downloadApprovedStatus={appDetails.vendorApproved}
									installStatus={appDetails.installStatus}
									successHandler={(newStatus: InstallStatus) => {
										setAppDetails({
											...appDetails,
											installStatus: newStatus,
										});
										reload();
									}}
									buttonsAs={EditButton}
									isForGallery
								/>
							</Box>
						</Flex>
					)}

				{canUpdateTokens && (
					<Flex color={showroomText} justify={isMobile ? "center" : "left"} pt="5px" h="32px">
						<ShowroomUpdateTokensButton
							appDetails={appDetails}
							userSecurity={userSecurity}
							setAppDetails={setAppDetails}
						/>
					</Flex>
				)}
			</Stack>

			{(showNotImplementingNowTag || canRequest) && (
				<Wrap spacing={0} justify={isMobile ? "center" : "left"} pt="5px">
					{showNotImplementingNowTag && (
						<>
							<Flex
								color={showroomText}
								alignItems="center"
								pr="3px"
								fontWeight="400"
								fontSize=""
							>
								<TimeIcon color={textColor} mr="3px" /> Not Implementing Now{" "}
							</Flex>
							<Box display="inline" color={"gray.500"} fontStyle="italic" pr="5px">
								set{" "}
								{new Date(appDetails.notImplementingNowDetails.instant).toLocaleDateString()}
								{showNotNowUserName
									? " by " + appDetails.notImplementingNowDetails.userName
									: ""}{" "}
								<AOTooltip
									helptext={
										<NotImplementingNowInfoBody
											latestAppVersionId={appDetails.appId!}
											notNowDecision={appDetails.notImplementingNowDetails}
											userSecurity={userSecurity}
										/>
									}
									boxProps={{ paddingX: "5px" }}
								>
									<QuestionOutlineIcon color={"gray.500"} />
								</AOTooltip>
							</Box>
						</>
					)}

					{canRequest && (
						<NotImplementingNowButtonWrapper
							appDetails={appDetails}
							userSecurity={userSecurity}
							setAppDetails={setAppDetails}
							buttonProps={actionButtonProps}
						/>
					)}
				</Wrap>
			)}
			{(showNominationCountTag || canNominate) && (
				<Wrap spacing={0} justify={isMobile ? "center" : "left"} pt="5px">
					{showNominationCountTag && (
						<>
							<Flex
								color={showroomText}
								alignItems="center"
								pr="3px"
								fontWeight="400"
								fontSize=""
							>
								<BiHeart style={{ color: textColor, marginRight: "3px" }} />

								{`${appDetails.interestedCount} ${pluralIfNeeded(
									"Nomination",
									appDetails.interestedCount,
								)} `}
							</Flex>
							<Flex alignItems="center" color={"gray.500"} fontStyle="italic" pr="5px">
								<AOTooltip
									helptext={
										appDetails.interestedOn
											? `You nominated this product on ${appDetails.interestedOn}`
											: nominationCountTitle
									}
									boxProps={{ paddingX: "5px" }}
								>
									<QuestionOutlineIcon color={"gray.500"} />
								</AOTooltip>
							</Flex>
						</>
					)}

					{canNominate && (
						<NominateButton
							appDetails={appDetails}
							userSecurity={userSecurity}
							setAppDetails={setAppDetails}
							buttonProps={actionButtonProps}
						/>
					)}
				</Wrap>
			)}
			{appDetails.appModel.agreementId > 0 && userSecurity.canPurchase && (
				<Wrap spacing={0} justify={isMobile ? "center" : "left"} pt="5px">
					<Flex color={showroomText} alignItems="center" pr="3px" fontWeight="400" fontSize="">
						<AOLink
							url={
								isShowroomPublic
									? `${showroomBaseUrl}VendorServices/api/Showroom/AppAgreement?termId=${appDetails.appModel.agreementId}&download=false`
									: `${baseUrl}Developer/AppAgreement?termId=${appDetails.appModel.agreementId}`
							}
							isExternal
						>
							View App Agreement
						</AOLink>
					</Flex>
				</Wrap>
			)}
		</VStack>
	);
});

const EditButton: ComponentWithAs<"button", ButtonProps> = (props: ButtonProps) => {
	const { leftIcon, children, ...rest } = props;

	return (
		<Button {...rest} variant="outline" fontWeight="400" fontSize="1rem" size="sm" w="100%">
			{children}
		</Button>
	);
};
